import { QueryClient, QueryKey, useQuery, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import { GetBidReportAnswersQuery } from "lib/generated/app-service-gql/graphql";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "lib/hooks/api/utils";
import { useGraphQlQuery } from "../../useGraphQlClient";
import { getBidQualContentForExternalUser } from "../public/getBidQualContentForExternalUser";
import { useExternalBidQuery } from "../public/useExternalBidQuery";

const getBidReportAnswers = graphql(`
  query getBidReportAnswers($id: String!) {
    getBidReportAnswers(id: $id) {
      result {
        groupName
        questions {
          answerId
          autoQualify
          title
          order
          answer {
            answer
          }
          sources {
            fileName
            documentId
            type
            id
            title
            url
            chunkNumber
            documentType
            filePath
            isDeleted
            citationId
            locations {
              pageNumber
              bbox {
                x
                y
                width
                height
              }
            }
          }
        }
      }
    }
  }
`);

export type BidNoBidResponse = GetBidReportAnswersQuery["getBidReportAnswers"]["result"];
export type BidReportQuestionGroup = BidNoBidResponse[number];
export function useGetBidReportAnswers(
  id: string,
  options?: UseQueryOptions<GetBidReportAnswersQuery, unknown, GetBidReportAnswersQuery, QueryKey>,
) {
  const { useExternalQuery, urlShareSignature } = useExternalBidQuery();

  // Primary query for logged-in user
  const { data: reportData, ...rest } = useGraphQlQuery(
    ["get_bid_report_answers", id],
    getBidReportAnswers,
    [{ id }],
    {
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      ...options,
      enabled: !useExternalQuery,
    },
  );

  // External access fallback (public view)
  const { data: nonLoggedInData, ...other } = useQuery(
    ["get_bid_report_answers", id],
    () => getBidQualContentForExternalUser(id, urlShareSignature),
    {
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      enabled: useExternalQuery,
    },
  );

  if (useExternalQuery) {
    return {
      data: nonLoggedInData?.result,
      ...other,
    };
  } else {
    return {
      data: reportData?.getBidReportAnswers?.result,
      ...rest,
    };
  }
}

export function updateBidReportAnswersCache(
  queryClient: QueryClient,
  bidReportId: string,
  updater: (
    bidReport: GetBidReportAnswersQuery["getBidReportAnswers"],
  ) => GetBidReportAnswersQuery["getBidReportAnswers"],
) {
  const key = ["get_bid_report_answers", bidReportId];
  queryClient.setQueryData<GetBidReportAnswersQuery>(key, (prev) => {
    // Seed an empty shape when the cache hasn't been populated yet — e.g. the
    // user starts orchestrator from the bid summary page before
    // useGetBidReportAnswers has ever mounted for this report id. Without
    // this, every streaming projection and per-section success hydration
    // silently no-ops, and when the user later navigates to the report page
    // they see whatever the server returns at that moment instead of the
    // streamed content.
    const existing: GetBidReportAnswersQuery["getBidReportAnswers"] = prev?.getBidReportAnswers ?? {
      result: [],
    };

    return { getBidReportAnswers: updater(existing) };
  });
}

// Patches a single answer in the cached get_bid_report_answers query by its
// answerId, leaving every other question untouched. Used whenever a report
// entry's answer changes (e.g. an edit) so the report reflects the new answer
// without a refetch.
export function updateBidReportAnswerById(
  queryClient: QueryClient,
  bidReportId: string,
  answerId: string,
  answer: string,
) {
  updateBidReportAnswersCache(queryClient, bidReportId, (bidReport) => ({
    ...bidReport,
    result: bidReport.result.map((group) => ({
      ...group,
      questions: group.questions.map((question) =>
        question.answerId === answerId
          ? { ...question, answer: { ...question.answer, answer } }
          : question,
      ),
    })),
  }));
}
